import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import {
  JobApplicationType,
  JobApplicationResponseType,
} from "@/app/career/view-jobs/[id]/type";

import {
  ConvertPositionType,
  GetGeneratedProfilesType,
  ContactDetailsType,
  PatchVerifyContactDetailsType,
  PositionType,
  PatchVerifyPersonalInformationDetailsType,
  PatchVerifyProfessionalInformationType,
  PersonalInformationType,
  ProfessionalInformation,
  SaveGeneratedProfilesType,
  MyProfileType,
  ProfilePictureType,
  DimensionsType,
  NextCareerStepType,
  PatchNextCareerStepParams,
  PostNextCareerStepParams,
  PatchVerifyCurrentWorkingPositionType,
  PatchVerifyPastPositionType,
  PostVerifyPastPositionType,
  LanguageEnum,
  GeoCodeType,
  CompanyType,
  ExpertiseType,
} from "@/services/type";
import {
  GET_GENERATED_PROFILE_QUERY_KEY,
  getNextCareerStepQuery,
  myProfileQuery,
} from "@/react-query/queries";
import { useAccountStore } from "@/stores";
import {
  autoCompleteCompanies,
  autoCompleteExpertises,
  autoCompleteGeoCode,
} from "@/services/autoComplete";
import { getBase64 } from "@/utils/others";
import {
  patchMyProfileContactDetails,
  patchMyProfileCurrentWorkingPosition,
  patchVerifyCurrentWorkingPosition,
  patchPersonalInformation,
  patchPersonalInformationPayment,
  patchProfessionalInformation,
  patchVerifyContactDetails,
  patchVerifyPersonalInformation,
  patchVerifyProfessionalInformation,
  postAccountUploads,
  postGeneratedProfiles,
  postGeneratedProfilesPrivacy,
  postMyProfileConvertPosition,
  postMyProfilePastPosition,
  postTopCVReviewRequests,
  postVerifyConvertPosition,
  postVerifyPastPosition,
  saveGeneratedProfiles,
  postMyProfileProfilePicture,
  postVerifyProfilePicture,
  patchVerifyProfilePicture,
  patchMyProfileProfilePicture,
  deleteMyProfileProfilePicture,
  deleteVerifyProfilePicture,
  patchNextCareerStep,
  postNextCareerStep,
  patchVerifyPastWorkingPosition,
  deleteMyProfilePastPosition,
  deleteVerifyPastPosition,
  postJobApplication,
  postJobBookmark,
  deleteJobBookmark,
} from "@/services/career";
import { sizeError } from "@/pages/complete";
import { useIsMyProfile } from "@/hooks/useIsMyProfile";

// =========================================
// Types
// =========================================
export interface UseUploadCVDataType {
  stringifyId: string;
  identifier: string;
  fileName: string;
  stringifyFileSize: string;
}
interface StartExtractionParams {
  allowHeadhunters: boolean;
  allowTopResume: boolean;
  processingToken: string;
  uploadId: string;
}
interface PatchContactDetailsParams {
  phoneNumber: string;
}
interface PatchPersonalInformationParams {
  profilePicture?: string;
  pictureDimensions?: DimensionsType;
  genderId: number;
  name: string;
  lastName: string;
  city: string;
  countryId: number;
  dateOfBirth: string;
  geoCodeId: number;
}
type PatchPersonalInformationPaymentParams = {
  genderId: number;
  name: string;
  lastName: string;
  city: string;
  countryId: number;
};
interface PatchProfessionalInformationParams {
  currentSalaryRangeId: number;
  managementExperienceId: number;
  skills: string[];
}
interface PatchWorkingPositionParams {
  identifier?: string | number;
  title?: string;
  companyName?: string;
  companyId?: number;
  startDate?: string;
  endDate?: string | null;
  geoCodeId?: number | null;
  countryId?: number;
  city?: string;
  careerLevelId?: number;
  functionId?: number;
  industryId?: number;
  description?: string;
}
interface UpdatePastWorkingPositionParams {
  identifier?: string | number;
  title?: string;
  companyName?: string;
  companyId?: number;
  startDate?: string;
  endDate?: string;
  geoCodeId?: number | null;
  countryId?: number;
  city?: string;
  careerLevelId?: number;
  functionId?: number;
  industryId?: number;
  description?: string;
}
interface PostConvertPositionParams {
  profileId: string;
  positionId: string;
}
interface AutoCompleteGeoCodeParamType {
  city: string;
  countryId: number;
}

// =========================================
// Options
// =========================================
// since we don't care about autoComplete query and response, I use zero cache time
const autoCompleteQueryOptions = {
  cacheTime: 0,
};

// =========================================
// Hooks
// =========================================
export function useUploadCV() {
  const { t } = useTranslation();
  return useMutation<UseUploadCVDataType, unknown, File>({
    async mutationFn(file: File) {
      const { name: fileName, type: fileType, size: fileSize } = file;

      // prevent uploading more than 3.0 MB
      if (fileSize > 3_000_000) throw Error(t(sizeError) as string);

      const data = await getBase64(file);
      const { id } = await postAccountUploads(
        {
          data,
          file_name: fileName,
          "Content-Type": fileType,
        },
        1,
        t("Lebenslauf"),
        // 422 = Format is not acceptable
        { silentSentry: [422] }
      );
      const stringifyId = String(id);
      const stringifyFileSize = String(fileSize);
      const { identifier } = await postGeneratedProfiles(stringifyId);

      return {
        stringifyId,
        identifier,
        fileName,
        stringifyFileSize,
      };
    },
    throwOnError: false,
  });
}
export function useStartExtractionAnimation() {
  return useMutation<void, unknown, StartExtractionParams>({
    async mutationFn({
      allowHeadhunters,
      allowTopResume,
      processingToken,
      uploadId,
    }) {
      const optInRequests = [
        postGeneratedProfilesPrivacy(processingToken, allowHeadhunters),
      ];

      if (allowTopResume) {
        optInRequests.push(postTopCVReviewRequests(uploadId));
      }

      await Promise.all(optInRequests);
    },
  });
}
export function useSaveGeneratedProfiles(profileId: string) {
  return useMutation<SaveGeneratedProfilesType, unknown>({
    async mutationFn() {
      return saveGeneratedProfiles(profileId);
    },
  });
}
export function useConvertPastPosition(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();
  return useMutation<ConvertPositionType, unknown, PostConvertPositionParams>({
    async mutationFn({ profileId, positionId }) {
      return isMyProfile
        ? postMyProfileConvertPosition(profileId, positionId)
        : postVerifyConvertPosition(profileId, positionId);
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const { currentPosition, pastPositions } = responseData;
      const oldData =
        queryClient.getQueryData<GetGeneratedProfilesType>(queryKey);
      if (oldData) {
        const newData: GetGeneratedProfilesType = {
          ...oldData,
          currentPosition: currentPosition,
          pastPositions: pastPositions,
        };
        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
}
export function usePatchContactDetails(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();

  return useMutation<
    ContactDetailsType | PatchVerifyContactDetailsType,
    unknown,
    PatchContactDetailsParams
  >({
    async mutationFn({ phoneNumber }) {
      return isMyProfile
        ? patchMyProfileContactDetails(phoneNumber)
        : patchVerifyContactDetails(profileId, phoneNumber);
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const { phoneNumber } = responseData;
      const oldData = queryClient.getQueryData<
        GetGeneratedProfilesType | MyProfileType
      >(queryKey);

      if (oldData) {
        const newData = {
          ...oldData,
          contactDetails: {
            ...oldData.contactDetails,
            phoneNumber,
          },
        };

        if (
          !isMyProfile &&
          (responseData as PatchVerifyContactDetailsType).hasMinimumProfile
        ) {
          (newData as GetGeneratedProfilesType).hasMinimumProfile = true;
        }

        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
}
export function usePatchCurrentWorkingPosition(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();

  return useMutation<
    PositionType | PatchVerifyCurrentWorkingPositionType,
    unknown,
    PatchWorkingPositionParams
  >({
    async mutationFn({
      identifier,
      title,
      companyName,
      companyId,
      startDate,
      geoCodeId,
      countryId,
      city,
      careerLevelId,
      functionId,
      industryId,
      description,
    }) {
      return isMyProfile
        ? patchMyProfileCurrentWorkingPosition(
            identifier,
            title,
            companyName,
            companyId,
            startDate,
            geoCodeId,
            countryId,
            city,
            careerLevelId,
            functionId,
            industryId,
            description
          )
        : patchVerifyCurrentWorkingPosition(
            profileId,
            identifier,
            title,
            companyName,
            companyId,
            startDate,
            geoCodeId,
            countryId,
            city,
            careerLevelId,
            functionId,
            industryId,
            description
          );
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const {
        identifier,
        title,
        companyName,
        companyId,
        startDate,
        endDate,
        geoCodeId,
        countryId,
        city,
        careerLevelId,
        functionId,
        industryId,
        description,
      } = responseData;
      const oldData = queryClient.getQueryData<
        GetGeneratedProfilesType | MyProfileType
      >(queryKey);

      if (oldData) {
        const newData = {
          ...oldData,
          currentPosition: {
            identifier,
            title,
            companyName,
            companyId,
            startDate,
            endDate,
            geoCodeId,
            countryId,
            city,
            careerLevelId,
            functionId,
            industryId,
            description,
          },
        };

        if (
          !isMyProfile &&
          (responseData as PatchVerifyCurrentWorkingPositionType)
            .hasMinimumProfile
        ) {
          (newData as GetGeneratedProfilesType).hasMinimumProfile = true;
        }

        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
}
export function useUpdatePastWorkingPosition(
  profileId: string,
  positionId: string,
  isConvert: boolean
) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();
  const { mutateAsync } = useConvertPastPosition(profileId);

  return useMutation<
    PositionType | PatchVerifyPastPositionType,
    unknown,
    UpdatePastWorkingPositionParams
  >({
    async mutationFn({
      identifier,
      title,
      companyName,
      companyId,
      startDate,
      endDate,
      geoCodeId,
      countryId,
      city,
      careerLevelId,
      functionId,
      industryId,
      description,
    }) {
      return isMyProfile
        ? postMyProfilePastPosition(
            identifier,
            title,
            companyName,
            companyId,
            startDate,
            endDate,
            geoCodeId,
            countryId,
            city,
            careerLevelId,
            functionId,
            industryId,
            description
          )
        : patchVerifyPastWorkingPosition(
            profileId,
            identifier,
            title,
            companyName,
            companyId,
            startDate,
            endDate,
            geoCodeId,
            countryId,
            city,
            careerLevelId,
            functionId,
            industryId,
            description
          );
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const {
        identifier,
        title,
        companyName,
        companyId,
        startDate,
        endDate,
        geoCodeId,
        countryId,
        city,
        careerLevelId,
        functionId,
        industryId,
        description,
      } = responseData;

      if (isConvert && identifier) {
        mutateAsync({ profileId, positionId: String(identifier) });
      } else {
        const oldData = queryClient.getQueryData<
          GetGeneratedProfilesType | MyProfileType
        >(queryKey);

        if (oldData) {
          const newPastPositions = oldData.pastPositions;
          const foundedPositionIndex = oldData.pastPositions.findIndex(
            ({ identifier }) => {
              // in myProfile `identifier` is `string` while in CV2Profile is `number`
              // `positionId` is always string
              const correctFormOfPositionId = isMyProfile
                ? Number(positionId)
                : String(positionId);

              return identifier === correctFormOfPositionId;
            }
          );

          if (foundedPositionIndex !== -1) {
            newPastPositions[foundedPositionIndex] = {
              identifier,
              title,
              companyName,
              companyId,
              startDate,
              endDate,
              geoCodeId,
              countryId,
              city,
              careerLevelId,
              functionId,
              industryId,
              description,
            };
          }

          const newData = {
            ...oldData,
            pastPositions: newPastPositions,
          };

          if (
            !isMyProfile &&
            (responseData as PatchVerifyPastPositionType).hasMinimumProfile
          ) {
            (newData as GetGeneratedProfilesType).hasMinimumProfile = true;
          }

          queryClient.setQueryData(queryKey, newData);
        }
      }
    },
  });
}
export function useDeletePastWorkingPosition(
  profileId: string,
  positionId: string
) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();
  return useMutation<object, unknown>({
    async mutationFn() {
      return isMyProfile
        ? deleteMyProfilePastPosition(positionId)
        : deleteVerifyPastPosition(profileId, positionId);
    },
    onSuccess: () => {
      const key = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const oldData = queryClient.getQueryData<
        GetGeneratedProfilesType | MyProfileType
      >(key);
      if (oldData) {
        const oldPastPositions = oldData.pastPositions;
        const newPastPositions = oldPastPositions.filter(
          ({ identifier }) => String(identifier) !== positionId
        );
        const newData = {
          ...oldData,
          pastPositions: newPastPositions,
        };
        queryClient.setQueryData(key, newData);
      }
    },
  });
}
export function usePostPastPosition(profileId: string, isConvert: boolean) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();
  const { mutateAsync } = useConvertPastPosition(profileId);

  return useMutation<
    PositionType | PostVerifyPastPositionType,
    unknown,
    PatchWorkingPositionParams
  >({
    async mutationFn({
      identifier,
      title,
      companyName,
      companyId,
      startDate,
      endDate,
      geoCodeId,
      countryId,
      city,
      careerLevelId,
      functionId,
      industryId,
      description,
    }) {
      return isMyProfile
        ? postMyProfilePastPosition(
            identifier,
            title,
            companyName,
            companyId,
            startDate,
            endDate,
            geoCodeId,
            countryId,
            city,
            careerLevelId,
            functionId,
            industryId,
            description
          )
        : postVerifyPastPosition(
            profileId,
            identifier,
            title,
            companyName,
            companyId,
            startDate,
            endDate,
            geoCodeId,
            countryId,
            city,
            careerLevelId,
            functionId,
            industryId,
            description
          );
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const {
        identifier,
        title,
        companyName,
        companyId,
        startDate,
        endDate,
        geoCodeId,
        countryId,
        city,
        careerLevelId,
        functionId,
        industryId,
        description,
      } = responseData;
      if (isConvert && identifier) {
        mutateAsync({ profileId, positionId: String(identifier) });
      } else {
        const oldData = queryClient.getQueryData<
          GetGeneratedProfilesType | MyProfileType
        >(queryKey);

        if (oldData) {
          const newData = {
            ...oldData,
            pastPositions: [
              ...oldData.pastPositions,
              {
                identifier,
                title,
                companyName,
                companyId,
                startDate,
                endDate,
                geoCodeId,
                countryId,
                city,
                careerLevelId,
                functionId,
                industryId,
                description,
              },
            ],
          };

          if (
            !isMyProfile &&
            (responseData as PostVerifyPastPositionType).hasMinimumProfile
          ) {
            (newData as GetGeneratedProfilesType).hasMinimumProfile = true;
          }

          queryClient.setQueryData(queryKey, newData);
        }
      }
    },
  });
}
export function usePatchPersonalInformation(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();

  return useMutation<
    PersonalInformationType | PatchVerifyPersonalInformationDetailsType,
    unknown,
    PatchPersonalInformationParams
  >({
    async mutationFn({
      genderId,
      name,
      lastName,
      city,
      countryId,
      dateOfBirth,
      geoCodeId,
    }) {
      return isMyProfile
        ? patchPersonalInformation(
            genderId,
            name,
            lastName,
            city,
            countryId,
            dateOfBirth,
            geoCodeId
          )
        : patchVerifyPersonalInformation(
            profileId,
            genderId,
            name,
            lastName,
            city,
            countryId,
            dateOfBirth,
            geoCodeId
          );
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const {
        genderId,
        name,
        lastName,
        city,
        countryId,
        dateOfBirth,
        geoCodeId,
      } = responseData;
      const oldData = queryClient.getQueryData<
        GetGeneratedProfilesType | MyProfileType
      >(queryKey);
      if (oldData) {
        const newData = {
          ...oldData,
          personalInformation: {
            ...oldData.personalInformation,
            genderId,
            name,
            lastName,
            city,
            countryId,
            dateOfBirth,
            geoCodeId,
          },
        };

        if (
          !isMyProfile &&
          (responseData as PatchVerifyPersonalInformationDetailsType)
            .hasMinimumProfile
        ) {
          (newData as GetGeneratedProfilesType).hasMinimumProfile = true;
        }
        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
}

export function usePatchPersonalInformationPayment() {
  const queryClient = useQueryClient();

  return useMutation<
    PersonalInformationType,
    unknown,
    PatchPersonalInformationPaymentParams
  >({
    async mutationFn({ genderId, name, lastName, city, countryId }) {
      return patchPersonalInformationPayment(
        genderId,
        name,
        lastName,
        city,
        countryId
      );
    },
    onSuccess(responseData) {
      const oldData = queryClient.getQueryData<MyProfileType>(
        myProfileQuery.queryKey
      );
      if (!oldData) return;

      const { genderId, name, lastName, city, countryId } = responseData;

      queryClient.setQueryData(myProfileQuery.queryKey, {
        ...oldData,
        personalInformation: {
          ...oldData.personalInformation,
          genderId,
          name,
          lastName,
          city,
          countryId,
        },
      });
    },
  });
}

export function usePatchProfessionalInformation(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();

  return useMutation<
    ProfessionalInformation | PatchVerifyProfessionalInformationType,
    unknown,
    PatchProfessionalInformationParams
  >({
    async mutationFn({ currentSalaryRangeId, managementExperienceId, skills }) {
      return isMyProfile
        ? patchProfessionalInformation(
            currentSalaryRangeId,
            managementExperienceId,
            skills
          )
        : patchVerifyProfessionalInformation(
            profileId,
            currentSalaryRangeId,
            managementExperienceId,
            skills
          );
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const { currentSalaryRangeId, managementExperienceId, skills } =
        responseData;
      const oldData = queryClient.getQueryData<
        GetGeneratedProfilesType | MyProfileType
      >(queryKey);

      if (oldData) {
        const newData = {
          ...oldData,
          professionalInformation: {
            currentSalaryRangeId,
            managementExperienceId,
            skills,
          },
        };

        if (
          !isMyProfile &&
          (responseData as PatchVerifyProfessionalInformationType)
            .hasMinimumProfile
        ) {
          (newData as GetGeneratedProfilesType).hasMinimumProfile = true;
        }

        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
}
export function usePostProfilePicture(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();

  return useMutation<ProfilePictureType, unknown, ProfilePictureType>({
    async mutationFn({ picture, dimensions }) {
      return isMyProfile
        ? postMyProfileProfilePicture(picture, dimensions, {
            // image size is not standard
            silentSentry: [422],
          })
        : postVerifyProfilePicture(profileId, picture, dimensions);
    },
    onSuccess(responseData) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const { picture, dimensions } = responseData;
      const oldData =
        queryClient.getQueryData<GetGeneratedProfilesType>(queryKey);

      if (oldData) {
        const newData: GetGeneratedProfilesType = {
          ...oldData,
          personalInformation: {
            ...oldData.personalInformation,
            profilePicture: picture,
            pictureDimensions: dimensions,
          },
        };
        queryClient.setQueryData(queryKey, newData);
      }
    },
    throwOnError: false,
  });
}
export function usePatchProfilePicture(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();

  return useMutation<
    ProfilePictureType | DimensionsType,
    unknown,
    ProfilePictureType
  >({
    async mutationFn({ dimensions }) {
      return isMyProfile
        ? patchMyProfileProfilePicture(dimensions)
        : patchVerifyProfilePicture(profileId, dimensions);
    },
    onSuccess(responseData, { picture }) {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      let dimensions;
      isMyProfile
        ? ({ dimensions } = responseData as ProfilePictureType)
        : (dimensions = responseData as DimensionsType);
      const oldData =
        queryClient.getQueryData<GetGeneratedProfilesType>(queryKey);
      if (oldData) {
        const newData: GetGeneratedProfilesType = {
          ...oldData,
          personalInformation: {
            ...oldData.personalInformation,
            profilePicture: picture,
            pictureDimensions: dimensions,
          },
        };
        queryClient.setQueryData(queryKey, newData);
      }
    },

    throwOnError: false,
  });
}
export function useDeleteProfilePicture(profileId: string) {
  const isMyProfile = useIsMyProfile();
  const queryClient = useQueryClient();

  return useMutation<ProfilePictureType, ProfilePictureType>({
    async mutationFn() {
      return isMyProfile
        ? deleteMyProfileProfilePicture()
        : deleteVerifyProfilePicture(profileId);
    },
    onSuccess() {
      const queryKey = isMyProfile
        ? myProfileQuery.queryKey
        : [GET_GENERATED_PROFILE_QUERY_KEY, { profileId }];
      const oldData =
        queryClient.getQueryData<GetGeneratedProfilesType>(queryKey);
      if (oldData) {
        const newData: GetGeneratedProfilesType = {
          ...oldData,
          personalInformation: {
            ...oldData.personalInformation,
            profilePicture: "",
            pictureDimensions: {},
          },
        };
        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
}
export function useCreateOrUpdateNextCareerStep() {
  const queryClient = useQueryClient();
  return useMutation<
    NextCareerStepType,
    unknown,
    PatchNextCareerStepParams & PostNextCareerStepParams
  >({
    async mutationFn({
      salary_range_id,
      travel_willingness_level_id,
      willing_to_move,
      locations,
      career_level_ids,
      function_ids,
      industry_ids,
      remote_type_ids,
      country_ids,
      careerId,
    }) {
      return Boolean(careerId)
        ? patchNextCareerStep({
            careerId,
            salary_range_id,
            industry_ids,
            function_ids,
            career_level_ids,
            travel_willingness_level_id,
            willing_to_move,
            locations,
            remote_type_ids,
            country_ids,
          })
        : postNextCareerStep({
            salary_range_id,
            industry_ids,
            function_ids,
            career_level_ids,
            travel_willingness_level_id,
            willing_to_move,
            locations,
            remote_type_ids,
          });
    },
    onSuccess(responseData) {
      queryClient.setQueryData(getNextCareerStepQuery.queryKey, responseData);
    },
  });
}
export function useAutoCompleteGeoCode() {
  const {
    accountDetails: { language },
  } = useAccountStore();

  return useMutation<GeoCodeType[], unknown, AutoCompleteGeoCodeParamType>({
    ...autoCompleteQueryOptions,
    async mutationFn({ city, countryId }) {
      const languageCode = language?.code ?? LanguageEnum.deutsch;

      return autoCompleteGeoCode({
        queryParams: {
          location: city,
          location_country_id: String(countryId),
          locale: languageCode,
          type: "point",
        },
      });
    },
  });
}
export function useAutoCompleteCompany() {
  return useMutation<CompanyType[], unknown, string>({
    ...autoCompleteQueryOptions,
    async mutationFn(query) {
      if (!query) return [];

      return autoCompleteCompanies({
        queryParams: {
          complete: encodeURIComponent(query),
        },
      });
    },
  });
}
export function useAutoCompleteExpertise() {
  const {
    accountDetails: { language },
  } = useAccountStore();

  return useMutation<ExpertiseType[], unknown, string>({
    ...autoCompleteQueryOptions,
    async mutationFn(query) {
      const languageCode = language?.code ?? LanguageEnum.deutsch;
      if (!query) return [];
      return autoCompleteExpertises({
        queryParams: {
          complete: encodeURIComponent(query),
          lang: languageCode,
        },
      });
    },
  });
}

export function useSubmitJobApplication() {
  return useMutation<JobApplicationResponseType, unknown, JobApplicationType>({
    async mutationFn({
      job_id,
      account_upload_ids,
      cover_letter,
      profile_accessible,
    }) {
      return postJobApplication(job_id, {
        account_upload_ids,
        cover_letter,
        profile_accessible,
      });
    },
  });
}

export const useCreateJobBookmark = () => {
  return useMutation<
    { id: number },
    unknown,
    { jobId: number; accountId: number }
  >({
    mutationFn: async ({ jobId, accountId }) =>
      postJobBookmark(jobId, accountId),
    throwOnError: false,
  });
};

export const useDeleteJobBookmark = () => {
  return useMutation<
    { message?: string },
    Error,
    { bookmarkId: number; accountId: number }
  >({
    mutationFn: ({ bookmarkId, accountId }) => {
      return deleteJobBookmark(bookmarkId, accountId);
    },
    throwOnError: false,
  });
};
